/**
 * Who may do what in the Employees module, in one place.
 *
 * The menu, the route guard, the toolbar and the row actions all ask here, so
 * the answer is the same in all four and a rule changes once. The codenames
 * are the backend's, and the groups that hold them are seeded there:
 *
 * | role               | open | add / edit | password request | activity log |
 * |--------------------|------|------------|------------------|--------------|
 * | associate          |  no  |     no     |        no        |      no      |
 * | supervisor         | yes  |    yes     |       yes        |      no      |
 * | manager            | yes  |    yes     |       yes        |     yes      |
 * | superuser / owner  | yes  |    yes     |       yes        |     yes      |
 *
 * As everywhere else, the answer is a hint for what to draw: the API refuses
 * on its own, so a control hidden here is one that would have failed anyway.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { profileQuery } from "@/services/queries";
import { can } from "@/utils/permissions";
import type { Profile } from "@/meta/types";

export const EMPLOYEE_PERMISSION = {
  /** Open the Employees screen. */
  view: "view__user",
  add: "add__user",
  /** Edit an account, and resolve its password requests. */
  manage: "change__user",
  /** The whole estate's history — a manager's, not a supervisor's. */
  log: "view__employee_activity_log",
} as const;

export interface EmployeeAccess {
  /** Supervisor and above. Anything false here means Unauthorized. */
  canOpen: boolean;
  canAdd: boolean;
  canManage: boolean;
  /** Approving and rejecting a password request is the manage right. */
  canResolveRequests: boolean;
  /** Manager and above. */
  canOpenLog: boolean;
}

export function employeeAccess(profile: Profile | undefined): EmployeeAccess {
  const manage = can(profile, EMPLOYEE_PERMISSION.manage);
  return {
    canOpen: can(profile, EMPLOYEE_PERMISSION.view),
    canAdd: can(profile, EMPLOYEE_PERMISSION.add),
    canManage: manage,
    canResolveRequests: manage,
    canOpenLog: can(profile, EMPLOYEE_PERMISSION.log),
  };
}

/**
 * False on every count until the profile lands, so nothing flashes into view.
 *
 * Memoised on the profile: callers put the answers straight into a `useMemo`
 * for a screen config, and a fresh object every render would rebuild it on
 * every render.
 */
export function useEmployeeAccess(): EmployeeAccess {
  const { data: profile } = useQuery(profileQuery());
  return useMemo(() => employeeAccess(profile), [profile]);
}

/**
 * Whether a row is staffed at the rank whose password requests queue up here.
 *
 * Only an associate's request waits for a supervisor: everyone above them is
 * emailed a temporary password directly at sign-in. "Clerk" is the same rank
 * under an older name, and some clubs still spell it that way.
 */
export function isAssociateRank(row: { is_superuser: boolean; groups: { name: string }[] }) {
  if (row.is_superuser) return false;
  if (row.groups.length === 0) return true; // No group is the default group.
  return row.groups.every((group) => /\b(associate|clerk)\b/i.test(group.name));
}

/**
 * The Password Request row action appears only when all three hold: the row is
 * actually waiting on one, it is an associate's (nobody else's queues here),
 * and the person looking may resolve it.
 */
export function showsPasswordRequest(
  row: { is_password_request: boolean; is_superuser: boolean; groups: { name: string }[] },
  access: EmployeeAccess,
) {
  return row.is_password_request && isAssociateRank(row) && access.canResolveRequests;
}
